import * as d3Interpolate from "d3-interpolate"

import { LcAddress } from "@/domains/core"

/**
 * Generate a range of 10 blues from dark to light using D3 interpolation
 * These colors stay on brand with the application's blue color scheme
 * Range goes from dark blue (primary-700) to light blue (primary-300)
 * 
 * Primary colors from design system:
 * - primary-300: hsl(227, 100%, 75%) - light blue
 * - primary-400: hsl(227, 100%, 65%) - medium-light blue  
 * - primary-600: hsl(240, 100%, 36%) - medium-dark blue
 * - primary-700: hsl(240, 100%, 29%) - dark blue
 */
function generateBluePalette(): string[] {
  // Dark blue - primary-700 equivalent: using brand color #1A56CD
  const darkBlue = "rgb(26, 86, 205)" // Brand dark blue #1A56CD
  // Light blue - primary-300 equivalent: hsl(227, 100%, 75%) ≈ rgb(141, 191, 255)
  const lightBlue = "rgb(141, 191, 255)"

  const palette: string[] = []
  for (let i = 0; i < 10; i++) {
    const t = i / 9 // Interpolation factor from 0 to 1
    // Use D3's interpolateRgb to create smooth gradient from dark to light blue
    // Index 0 = darkest (highest percentage), Index 9 = lightest (lowest percentage)
    const interpolated = d3Interpolate.interpolateRgb(darkBlue, lightBlue)(t)
    palette.push(interpolated)
  }

  return palette
}

const BLUE_PALETTE = generateBluePalette()

/**
 * Get a color from the blue palette based on index (0 = darkest, 9 = lightest)
 * Colors are assigned based on vote percentage order: highest % gets darkest, lowest % gets lightest
 *
 * @param index - The index in the sorted vote list (0 = highest %, higher = lower %)
 * @returns RGB color string (e.g., "rgb(26, 86, 205)")
 */
export function getGaugeColorByIndex(index: number): string {
  // Clamp index to palette bounds and assign from darkest (index 0) to lightest
  const clampedIndex = Math.min(Math.max(0, index), BLUE_PALETTE.length - 1)
  return BLUE_PALETTE[clampedIndex] ?? BLUE_PALETTE[0]!
}
